<template>
    <div class="notices-page">
        <!-- Header Bar -->
        <div class="notices-header">
            <div class="notices-header-left">
                <div class="notices-icon-wrap">
                    <i class="fas fa-bullhorn"></i>
                </div>
                <h2 class="notices-title">Notices</h2>
                <span class="notices-count" v-if="notices.length">{{ notices.length }}</span>
            </div>
            <div class="notices-header-right">
                <AllowPermission :permission="PERMISSION_CODES.CREATE_NOTICES">
                    <button class="notices-add-btn" @click="showForm = true">
                        <i class="fas fa-plus"></i>
                        New Notice
                    </button>
                </AllowPermission>
            </div>
        </div>

        <!-- Right Side Modal Form -->
        <RightSideModal
            :show="showForm"
            :title="editMode ? 'Edit Notice' : 'New Notice'"
            @close="closeForm"
        >
            <Form
                :fields="createFields"
                title="Notices"
                submitUrl="/notices"
                updateRootUrl="/notices"
                @success="handleCreateSuccess"
                @cancel="closeForm"
                :editMode="editMode"
                :editData="editData"
            />
        </RightSideModal>

        <!-- Loading -->
        <div v-if="loading" class="notices-loading">
            <div class="notices-spinner"></div>
            <span>Loading notices...</span>
        </div>

        <!-- Empty -->
        <div v-else-if="!notices.length" class="notices-empty">
            <div class="notices-empty-icon"><i class="fas fa-bullhorn"></i></div>
            <h3>No notices yet</h3>
            <p>Create a notice to get started</p>
        </div>

        <!-- Cards Grid -->
        <div v-else class="notices-grid">
            <div
                class="notice-card"
                v-for="(notice, idx) in notices"
                :key="notice.id"
                :style="{ animationDelay: `${idx * 0.02}s` }"
            >
                <div class="notice-card-top">
                    <div class="notice-card-icon" :class="getNoticeColor(idx)">
                        <i class="fas fa-bullhorn"></i>
                    </div>
                    <div class="notice-card-info">
                        <h4 class="notice-card-title">{{ notice.title }}</h4>
                        <div class="notice-card-time">
                            <i class="fas fa-calendar-alt"></i>
                            {{ notice.createdAtFormatted1 || formatDate(notice.createdAt) }}
                        </div>
                    </div>
                </div>
                <p class="notice-card-desc" v-if="notice.description">{{ notice.description }}</p>
                <div class="notice-card-attachments" v-if="notice.attachmentUrl1 || notice.attachmentUrl2 || notice.attachmentUrl3">
                    <a v-if="notice.attachmentUrl1" :href="notice.attachmentUrl1" target="_blank" class="notice-attach-chip">
                        <i class="fas fa-paperclip"></i> Attachment 1
                    </a>
                    <a v-if="notice.attachmentUrl2" :href="notice.attachmentUrl2" target="_blank" class="notice-attach-chip">
                        <i class="fas fa-paperclip"></i> Attachment 2
                    </a>
                    <a v-if="notice.attachmentUrl3" :href="notice.attachmentUrl3" target="_blank" class="notice-attach-chip">
                        <i class="fas fa-paperclip"></i> Attachment 3
                    </a>
                </div>
                <div class="notice-card-actions">
                    <AllowPermission :permission="PERMISSION_CODES.EDIT_NOTICES">
                        <button class="notice-action-btn notice-btn-edit" @click="enableEditing(notice)">
                            <i class="fas fa-pen"></i> Edit
                        </button>
                    </AllowPermission>
                    <AllowPermission :permission="PERMISSION_CODES.DELETE_NOTICES">
                        <button class="notice-action-btn notice-btn-delete" @click="deleteNotice(notice.id)">
                            <i class="fas fa-trash-alt"></i> Delete
                        </button>
                    </AllowPermission>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Form from "../../components/crud/form.vue"
import RightSideModal from "../../components/layout/RightSideModal.vue"
import { createField, createColumn, formatIndianDate } from "../../utils/common"
import { http } from "@/utils/http"
import { PERMISSION_CODES } from "../../store/permission"
import AllowPermission from "../../components/layout/AllowPermission.vue"

export default {
    data() {
        return {
            loading: true,
            showForm: false,
            editMode: false,
            editData: null,
            PERMISSION_CODES,
            employees: [],
            notices: [],
            createFields: [
                createField("Title*", "title", "text", true),
                createField("Description*", "description", "text", true),
            ],
        }
    },
    components: { Form, RightSideModal, AllowPermission },
    async mounted() {
        await this.fetchEmployees()
        this.createFields = this.createFields.concat([
            createField(
                "Select if only for specific Employees",
                "onlyForEmployeeIdsCsv",
                "multiselect",
                false,
                "",
                this.employees.map((item) => ({
                    id: item.id,
                    title: `${item.firstName || ""} ${item.lastName || ""}`,
                }))
            ),
            createField("Select/update attachment 1", "attachmentUrl1", "image", false),
            createField("Select/update attachment 2", "attachmentUrl2", "image", false),
            createField("Select/update attachment 3", "attachmentUrl3", "image", false),
        ])
        await this.fetchNotices()
        this.loading = false
    },
    methods: {
        closeForm() {
            this.showForm = false
            this.editMode = false
            this.editData = null
        },
        formatDate(d) {
            if (!d) return ""
            return formatIndianDate(d)
        },
        getNoticeColor(idx) {
            const colors = ["notice-clr-blue", "notice-clr-purple", "notice-clr-amber", "notice-clr-teal", "notice-clr-rose"]
            return colors[idx % colors.length]
        },
        handleCreateSuccess() {
            this.closeForm()
            this.fetchNotices()
        },
        enableEditing(notice) {
            this.editMode = true
            this.editData = { ...notice }
            this.showForm = true
        },
        async fetchEmployees() {
            try {
                const res = await http.get("/employees?all=true&limited_fields=true")
                this.employees = res.data.result
            } catch (err) {
                console.log("error in fetching employees", err)
            }
        },
        async fetchNotices() {
            try {
                const res = await http.get("/notices")
                this.notices = res.data.result || res.data || []
            } catch (err) {
                console.log("error in fetching notices", err)
            }
        },
        async deleteNotice(id) {
            if (!confirm("Are you sure you want to delete this notice?")) return
            try {
                await http.delete(`/notices/${id}`)
                this.$toast.success("Notice deleted successfully")
                this.notices = this.notices.filter((n) => n.id !== id)
            } catch (err) {
                console.log("error deleting notice", err)
                this.$toast.error("Error deleting notice")
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.notices-page {
    animation: noticesFadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
@keyframes noticesFadeIn {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
}

/* ========== HEADER ========== */
.notices-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    padding: 10px 16px;
    margin-bottom: 12px;
    box-shadow: $shadowSm;
    flex-wrap: wrap;
}
.notices-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
}
.notices-icon-wrap {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: linear-gradient(135deg, $primaryLight, darken($primaryLight, 4%));
    color: $primaryColor;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
}
.notices-title {
    font-size: 15px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
}
.notices-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: $radiusFull;
    font-size: 11px;
    font-weight: 700;
    background: $primaryColor;
    color: white;
}
.notices-add-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border: none;
    border-radius: 6px;
    background: $primaryColor;
    color: white;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all $transitionFast;
    font-family: inherit;

    &:hover { background: $primaryDark; }
}

/* ========== LOADING / EMPTY ========== */
.notices-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 64px 16px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    box-shadow: $shadowSm;

    span { font-size: 13px; color: $gray400; font-weight: 500; }
}
.notices-spinner {
    width: 24px;
    height: 24px;
    border: 2.5px solid $gray200;
    border-top-color: $primaryColor;
    border-radius: 50%;
    animation: noticesSpin 0.7s linear infinite;
}
@keyframes noticesSpin { to { transform: rotate(360deg); } }

.notices-empty {
    text-align: center;
    padding: 64px 16px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    box-shadow: $shadowSm;

    h3 { font-size: 14px; font-weight: 700; color: $gray600; margin: 0 0 4px; }
    p { font-size: 12px; color: $gray400; margin: 0; }
}
.notices-empty-icon {
    width: 48px;
    height: 48px;
    border-radius: $radiusFull;
    background: $gray50;
    border: 1px solid $gray100;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 12px;

    i { font-size: 20px; color: $gray300; }
}

/* ========== CARD GRID ========== */
.notices-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
}

.notice-card {
    background: white;
    border: 1px solid $gray100;
    border-radius: 10px;
    padding: 14px;
    box-shadow: $shadowSm;
    display: flex;
    flex-direction: column;
    gap: 10px;
    animation: noticeSlideIn 0.25s cubic-bezier(0.4, 0, 0.2, 1) both;
    transition: all $transitionFast;

    &:hover {
        border-color: $gray200;
        box-shadow: $shadowMd;
        transform: translateY(-1px);
    }
}
@keyframes noticeSlideIn {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
}
.notice-card-top {
    display: flex;
    align-items: flex-start;
    gap: 10px;
}
.notice-card-icon {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;
}
.notice-clr-blue { background: #eff6ff; color: #2563eb; }
.notice-clr-purple { background: #f5f3ff; color: #7c3aed; }
.notice-clr-amber { background: #fffbeb; color: #d97706; }
.notice-clr-teal { background: #f0fdfa; color: #0d9488; }
.notice-clr-rose { background: #fff1f2; color: #e11d48; }

.notice-card-info {
    flex: 1;
    min-width: 0;
}
.notice-card-title {
    font-size: 13.5px;
    font-weight: 700;
    color: $gray800;
    margin: 0 0 3px;
    line-height: 1.3;
    word-break: break-word;
}
.notice-card-time {
    font-size: 11px;
    color: $gray400;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 4px;

    i { font-size: 10px; }
}
.notice-card-desc {
    font-size: 12.5px;
    color: $gray600;
    line-height: 1.45;
    margin: 0;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
.notice-card-attachments {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.notice-attach-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    border-radius: 5px;
    background: $gray50;
    border: 1px solid $gray100;
    font-size: 11px;
    color: $gray600;
    font-weight: 500;
    text-decoration: none;
    transition: all $transitionFast;

    i { font-size: 10px; color: $gray400; }
    &:hover {
        background: $primaryLight;
        border-color: rgba(0, 94, 137, 0.1);
        color: $primaryColor;
        i { color: $primaryColor; }
    }
}
.notice-card-actions {
    display: flex;
    gap: 6px;
    margin-top: auto;
    padding-top: 6px;
    border-top: 1px solid $gray100;
}
.notice-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border: 1px solid $gray200;
    border-radius: 5px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: all $transitionFast;
    font-family: inherit;
    background: white;

    i { font-size: 10px; }
}
.notice-btn-edit {
    color: $primaryColor;
    &:hover { background: $primaryLight; border-color: rgba(0, 94, 137, 0.15); }
}
.notice-btn-delete {
    color: $danger;
    &:hover { background: #fef2f2; border-color: rgba(220, 38, 38, 0.15); }
}

/* ========== RESPONSIVE ========== */
@media (max-width: 992px) {
    .notices-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 768px) {
    .notices-header { padding: 10px 12px; }
    .notices-grid { gap: 8px; }
    .notice-card { padding: 12px; }
}
@media (max-width: 480px) {
    .notices-grid { grid-template-columns: 1fr; }
    .notice-card { padding: 10px; }
    .notice-card-title { font-size: 13px; }
}
</style>
